import React, { useState, useEffect, useMemo } from 'react';
import {
    Layout, Form, Input, Button, Space, message, Switch, Tag, Divider,
    Tooltip, Popconfirm, Typography, Alert, Badge, Card, Select,
    InputNumber, Table, Tabs, Radio, Row, Col, Modal, Statistic
} from 'antd';
import {
    KeyOutlined, SaveOutlined, DeleteOutlined, CheckCircleOutlined,
    SearchOutlined, LinkOutlined, ApiOutlined, GlobalOutlined,
    SettingOutlined, TeamOutlined, CrownOutlined, LockOutlined,
    UnlockOutlined, ThunderboltOutlined, RobotOutlined, PlusOutlined,
    EditOutlined, CloudServerOutlined, WarningOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Sider, Content } = Layout;
const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const CUSTOM_PROVIDER_PLACEHOLDER_ID = '__new_custom__';

// 内置供应商预设（与 AIProviders.js 保持同步）
const BUILTIN_PROVIDERS = [
    { id: 'openai',      name: 'OpenAI',                icon: 'OAI', group: '国际', apiBase: 'https://api.openai.com/v1',                             models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'o3', 'o4-mini'] },
    { id: 'anthropic',   name: 'Anthropic (Claude)',    icon: 'ANT', group: '国际', apiBase: 'https://api.anthropic.com/v1',                           models: ['claude-opus-4-5', 'claude-sonnet-4-5', 'claude-haiku-4-5'] },
    { id: 'google',      name: 'Google Gemini',         icon: 'GEM', group: '国际', apiBase: 'https://generativelanguage.googleapis.com/v1beta/openai', models: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.0-flash'] },
    { id: 'deepseek',    name: 'DeepSeek',              icon: 'DS',  group: '国际', apiBase: 'https://api.deepseek.com/v1',                            models: ['deepseek-chat', 'deepseek-reasoner'] },
    { id: 'openrouter',  name: 'OpenRouter',            icon: 'OR',  group: '国际', apiBase: 'https://openrouter.ai/api/v1',                           models: ['openai/gpt-4o', 'deepseek/deepseek-chat'] },
    { id: 'ollama',      name: 'Ollama (本地)',          icon: 'OL',  group: '国际', apiBase: 'http://localhost:11434/v1',                              models: ['llama3.3', 'qwen3:30b', 'deepseek-r1:32b'] },
    { id: 'zhipu',       name: '智谱AI (GLM)',          icon: 'GLM', group: '中国', apiBase: 'https://open.bigmodel.cn/api/paas/v4',                   models: ['glm-4-plus', 'glm-4-air', 'glm-4-flash'] },
    { id: 'qwen',        name: '通义千问 (Qwen)',       icon: 'QW',  group: '中国', apiBase: 'https://dashscope.aliyuncs.com/compatible-mode/v1',      models: ['qwen3-max', 'qwen-plus', 'qwen-max', 'qwen-flash'] },
    { id: 'moonshot',    name: 'Moonshot / Kimi',       icon: 'KM',  group: '中国', apiBase: 'https://api.moonshot.cn/v1',                             models: ['moonshot-v1-128k', 'moonshot-v1-32k'] },
    { id: 'minimax',     name: 'MiniMax',               icon: 'MM',  group: '中国', apiBase: 'https://api.minimax.chat/v1',                             models: ['MiniMax-M2.5', 'abab6.5s-chat'] },
    { id: 'volcengine',  name: '字节豆包 (Volcengine)', icon: 'VOL', group: '中国', apiBase: 'https://ark.cn-beijing.volces.com/api/v3',               models: ['doubao-pro-32k', 'doubao-lite-32k'] },
    { id: 'baidu',       name: '百度文心 (ERNIE)',      icon: 'BD',  group: '中国', apiBase: 'https://qianfan.baidubce.com/v2',                         models: ['ernie-4.5-turbo-128k', 'ernie-3.5-8k'] },
    { id: 'siliconflow', name: '硅基流动',              icon: 'SF',  group: '中国', apiBase: 'https://api.siliconflow.cn/v1',                           models: ['deepseek-ai/DeepSeek-V3.2', 'Qwen/Qwen3-235B-A22B'] },
    { id: 'stepfun',     name: '阶跃星辰',              icon: 'ST',  group: '中国', apiBase: 'https://api.stepfun.com/v1',                              models: ['step-3-mini', 'step-2-16k'] },
    { id: 'baichuan',    name: '百川智能',              icon: 'BC',  group: '中国', apiBase: 'https://api.baichuan-ai.com/v1',                          models: ['Baichuan4-Turbo', 'Baichuan3-Turbo-128k'] },
    { id: CUSTOM_PROVIDER_PLACEHOLDER_ID, name: '+ 自定义供应商', icon: '+', group: '其他', apiBase: '', models: [], isCustomEntry: true, isPlaceholder: true },
];

const BUILTIN_PROVIDER_IDS = new Set(
    BUILTIN_PROVIDERS
        .filter(provider => provider.id !== CUSTOM_PROVIDER_PLACEHOLDER_ID)
        .map(provider => provider.id)
);

function normalizeModelList(input) {
    if (!Array.isArray(input)) return [];
    return input
        .map(item => String(item || '').trim())
        .filter(Boolean);
}

function normalizeProviderKey(displayName) {
    const base = String(displayName || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-+/g, '-');
    return base || `custom-${Date.now()}`;
}

const AI_POLICY_OPTIONS = [
    { value: 'all',    label: '全部用户可用',     color: 'green',  icon: <UnlockOutlined />,   desc: '所有登录用户均可调用系统AI，无需会员或积分门槛' },
    { value: 'member', label: '仅会员用户可用',   color: 'blue',   icon: <CrownOutlined />,    desc: '只有有效会员才可调用系统AI，并受套餐AI权限与额度控制' },
    { value: 'quota',  label: '配额制（旧模式）', color: 'orange', icon: <LockOutlined />,     desc: '由管理员手动为每位教师分配AI配额，未分配则无法使用' },
];

const SystemAIConfig = () => {
    const { user } = useAuth();
    const [globalConfigs, setGlobalConfigs] = useState([]);
    const [membershipPlans, setMembershipPlans] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedId, setSelectedId] = useState('deepseek');
    const [customModelsStr, setCustomModelsStr] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [aiPolicy, setAiPolicy] = useState('quota');
    const [pointsThreshold, setPointsThreshold] = useState(300);
    const [policyLoading, setPolicyLoading] = useState(false);
    const [planEditVisible, setPlanEditVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [planSaving, setPlanSaving] = useState(false);
    const [globalStatistics, setGlobalStatistics] = useState({
        total_calls: 0,
        total_tokens: 0,
        total_teachers: 0,
        today_calls: 0,
        today_tokens: 0,
        active_members: 0,
        member_rate: 0,
        avg_tokens_per_call: 0,
        total_points_balance: 0,
        expiring_members_7d: 0
    });

    const [globalForm] = Form.useForm();
    const [planForm] = Form.useForm();

    useEffect(() => {
        fetchGlobalConfigs();
        fetchMembershipPlans();
        fetchAiPolicy();
        fetchStats();
    }, []);

    const fetchGlobalConfigs = async () => {
        try {
            const res = await api.get('/ai-providers');
            if (res.data.success) setGlobalConfigs(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchMembershipPlans = async () => {
        try {
            const res = await api.get('/membership/plans');
            if (res.data.success) setMembershipPlans(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchAiPolicy = async () => {
        try {
            const res = await api.get('/membership/admin/ai-policy');
            if (res.data.success) {
                setAiPolicy(res.data.data?.policy || 'quota');
                setPointsThreshold(Number(res.data.data?.points_threshold) || 300);
            }
        } catch (e) { console.error(e); }
    };

    const fetchStats = async () => {
        try {
            const res = await api.get('/membership/admin/stats');
            if (res.data.success) setGlobalStatistics(res.data.data || {});
        } catch (e) { /* ignore */ }
    };

    // 供应商列表
    const providerCatalog = useMemo(() => {
        const builtinProviders = BUILTIN_PROVIDERS
            .filter(provider => provider.id !== CUSTOM_PROVIDER_PLACEHOLDER_ID)
            .map(provider => ({
                ...provider,
                isCustomEntry: !!provider.isCustomEntry
            }));

        const customProviders = globalConfigs
            .filter(provider => !BUILTIN_PROVIDER_IDS.has(provider.provider_name))
            .map(provider => ({
                id: provider.provider_name,
                name: provider.display_name || provider.provider_name,
                icon: '＋',
                group: '其他',
                apiBase: provider.api_base || '',
                models: normalizeModelList(provider.models),
                isCustomEntry: true
            }));

        return [
            ...builtinProviders,
            ...customProviders,
            BUILTIN_PROVIDERS.find(provider => provider.id === CUSTOM_PROVIDER_PLACEHOLDER_ID)
        ].filter(Boolean);
    }, [globalConfigs]);

    const selectedPreset = useMemo(
        () => providerCatalog.find(p => p.id === selectedId) || providerCatalog[0],
        [providerCatalog, selectedId]
    );
    const globalRecord = useMemo(
        () => globalConfigs.find(p => p.provider_name === selectedId),
        [selectedId, globalConfigs]
    );
    const policyOptions = useMemo(() => ([
        ...AI_POLICY_OPTIONS.slice(0, 2),
        {
            value: 'points',
            label: '积分达标可用',
            color: 'cyan',
            icon: <ThunderboltOutlined />,
            desc: '只有积分达到超级管理员设定门槛后，才可调用系统 AI'
        },
        ...AI_POLICY_OPTIONS.slice(2)
    ]), []);

    useEffect(() => {
        if (!selectedPreset) return;
        globalForm.setFieldsValue({
            display_name: globalRecord?.display_name || selectedPreset.name,
            api_key: '',
            api_base: globalRecord?.api_base || selectedPreset.apiBase,
            is_active: globalRecord ? !!globalRecord.is_active : false,
        });
        setCustomModelsStr(
            globalRecord?.models?.length
                ? JSON.stringify(globalRecord.models)
                : (selectedPreset.models?.length ? JSON.stringify(selectedPreset.models) : '')
        );
    }, [selectedId, globalConfigs, globalForm, selectedPreset, globalRecord]);

    const filteredProviders = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return providerCatalog.filter(p =>
            !q || p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q)
        );
    }, [providerCatalog, searchQuery]);

    const statsCards = useMemo(() => ([
        { title: '今日 AI 调用', value: globalStatistics.today_calls || 0, suffix: '次', icon: <ThunderboltOutlined />, color: '#1677ff' },
        { title: '今日 Token', value: globalStatistics.today_tokens || 0, icon: <ApiOutlined />, color: '#13c2c2' },
        { title: '本月 AI 调用', value: globalStatistics.total_calls || 0, suffix: '次', icon: <RobotOutlined />, color: '#722ed1' },
        { title: '本月累计 Token', value: globalStatistics.total_tokens || 0, icon: <ApiOutlined />, color: '#764ba2' },
        { title: '平均每次 Token', value: globalStatistics.avg_tokens_per_call || 0, icon: <GlobalOutlined />, color: '#fa8c16' },
        { title: '活跃会员数', value: globalStatistics.active_members || 0, suffix: '人', icon: <CrownOutlined />, color: '#52c41a' },
        { title: '会员覆盖率', value: globalStatistics.member_rate || 0, suffix: '%', icon: <TeamOutlined />, color: '#2f54eb' },
        { title: '积分总余额', value: globalStatistics.total_points_balance || 0, icon: <PlusOutlined />, color: '#eb2f96' },
        { title: '已配置供应商', value: globalConfigs.filter(c => c.is_active).length, suffix: '个', icon: <SettingOutlined />, color: '#52c41a' },
        { title: '教师总数', value: globalStatistics.total_teachers || 0, suffix: '人', icon: <TeamOutlined />, color: '#595959' },
        { title: '7天内到期会员', value: globalStatistics.expiring_members_7d || 0, suffix: '人', icon: <WarningOutlined />, color: '#fa541c' },
    ]), [globalConfigs, globalStatistics]);

    // 保存全局 API Key 配置
    const handleSaveGlobal = async (values) => {
        let models = normalizeModelList(selectedPreset.models);
        if (customModelsStr.trim()) {
            try {
                const parsed = JSON.parse(customModelsStr);
                if (!Array.isArray(parsed)) {
                    message.error('模型列表格式错误，必须是 JSON 数组');
                    return;
                }
                models = normalizeModelList(parsed);
            } catch { message.error('模型列表格式错误'); return; }
        }
        if (!values.display_name?.trim()) {
            message.error('自定义供应商需要填写显示名称');
            return;
        }
        if (selectedPreset?.isCustomEntry && models.length === 0) {
            message.error('请至少填写一个支持的模型，例如 ["gpt-4o"]');
            return;
        }
        setSaving(true);
        try {
            const providerName = globalRecord?.provider_name
                || (selectedPreset?.isCustomEntry
                    ? normalizeProviderKey(values.display_name)
                    : selectedPreset.id);
            const payload = {
                provider_name: providerName,
                display_name: values.display_name || selectedPreset.name,
                api_key: values.api_key || '',
                api_base: values.api_base || selectedPreset.apiBase,
                models,
                is_active: values.is_active ? 1 : 0,
            };
            if (globalRecord) {
                await api.put(`/ai-providers/${globalRecord.id}`, payload);
            } else {
                await api.post('/ai-providers', payload);
            }
            message.success('全局 API 配置已保存');
            await fetchGlobalConfigs();
            setSelectedId(providerName);
        } catch (e) {
            message.error(e.response?.data?.message || '保存失败');
        } finally { setSaving(false); }
    };

    const handleDeleteGlobal = async () => {
        if (!globalRecord) return;
        setDeleting(true);
        try {
            await api.delete(`/ai-providers/${globalRecord.id}`);
            message.success('已删除该全局配置');
            await fetchGlobalConfigs();
            setSelectedId('deepseek');
        } catch { message.error('鍒犻櫎澶辫触'); }
        finally { setDeleting(false); }
    };

    // AI 开放策略
    const handleSavePolicy = async (policy, threshold = pointsThreshold) => {
        setPolicyLoading(true);
        try {
            const normalizedThreshold = Math.max(Number(threshold) || 0, 0);
            await api.put('/membership/admin/ai-policy', {
                policy,
                points_threshold: normalizedThreshold
            });
            setAiPolicy(policy);
            setPointsThreshold(normalizedThreshold);
            message.success(`策略已更新：${policyOptions.find(o => o.value === policy)?.label || policy}`);
        } catch { message.error('保存失败'); }
        finally { setPolicyLoading(false); }
    };

    // 套餐 AI 权限配置
    const openPlanEdit = (plan) => {
        setEditingPlan(plan);
        planForm.setFieldsValue({
            ai_access: plan.ai_access != null ? plan.ai_access : 1,
            ai_token_monthly: plan.ai_token_monthly > 0 ? plan.ai_token_monthly : plan.token_quota || 30000,
            ai_points_per_1k: plan.ai_points_per_1k || 0,
        });
        setPlanEditVisible(true);
    };

    const handleSavePlan = async (values) => {
        setPlanSaving(true);
        try {
            await api.put('/membership/admin/plan-ai-config', {
                plan_key: editingPlan.plan_key,
                ai_access: values.ai_access ? 1 : 0,
                ai_token_monthly: values.ai_token_monthly,
                ai_points_per_1k: values.ai_points_per_1k,
            });
            message.success(`套餐“${editingPlan.name}”AI配置已保存`);
            setPlanEditVisible(false);
            fetchMembershipPlans();
        } catch (e) {
            message.error(e.response?.data?.message || '保存失败');
        } finally { setPlanSaving(false); }
    };

    const planColumns = [
        {
            title: '套餐',
            dataIndex: 'name',
            render: (v, r) => (
                <Space>
                    <Tag color={r.plan_code === 'max' ? 'gold' : r.plan_code === 'plus' ? 'purple' : r.plan_code === 'pro' ? 'blue' : 'default'}>
                        {r.plan_code?.toUpperCase()}
                    </Tag>
                    <Text strong>{v}</Text>
                </Space>
            )
        },
        {
            title: 'AI 使用权限',
            dataIndex: 'ai_access',
            render: v => v !== 0
                ? <Tag color="green" icon={<CheckCircleOutlined />}>可使用系统 AI</Tag>
                : <Tag color="red">禁用 AI</Tag>
        },
        {
            title: '月 Token 限额',
            key: 'token_limit',
            render: (_, r) => {
                const limit = r.ai_token_monthly;
                if (limit === -2) return <Tag color="gold">无限</Tag>;
                if (limit == null || limit === -1) return (r.token_quota === -1 ? <Tag color="gold">无限</Tag> : <Text>{(r.token_quota || 0).toLocaleString()}</Text>);
                return <Text style={{ color: '#764ba2', fontWeight: 600 }}>{limit.toLocaleString()}</Text>;
            }
        },
        {
            title: '积分价格（积分 / 1K Token）',
            dataIndex: 'ai_points_per_1k',
            render: v => v > 0 ? <Tag color="blue">{v} 积分 / 1K</Tag> : <Tag>免费（不扣积分）</Tag>
        },
        {
            title: '操作',
            render: (_, r) => (
                <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => openPlanEdit(r)}>
                    配置
                </Button>
            )
        }
    ];

    const groups = ['国际', '中国', '其他'];
    const currentPolicyInfo = policyOptions.find(o => o.value === aiPolicy);

    // Render
    return (
        <div style={{ padding: '0 0 40px' }}>
            {/* 顶部标题 */}
            <div style={{
                padding: '20px 24px 16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '0 0 16px 16px',
                marginBottom: 24
            }}>
                <Space align="center">
                    <CloudServerOutlined style={{ fontSize: 28, color: '#fff' }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#fff' }}>系统全局 AI 配置</Title>
                        <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
                            超级管理员专用，可统一配置系统级模型 API Key，并控制不同用户的 AI 调用权限
                        </Text>
                    </div>
                </Space>
            </div>

            <div style={{ padding: '0 24px' }}>
                {/* 统计卡片 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    {statsCards.map((item, i) => (
                        <Col key={i} xs={12} sm={8} lg={6} xl={6}>
                            <Card
                                size="small"
                                bodyStyle={{ padding: '12px 14px', minHeight: 88 }}
                                style={{ borderRadius: 12 }}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <div style={{ fontSize: 20, color: item.color, lineHeight: 1 }}>{item.icon}</div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: '18px' }}>{item.title}</div>
                                        <div style={{ fontSize: 18, fontWeight: 700, color: item.color, lineHeight: '26px' }}>
                                            {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                                            {item.suffix ? <span style={{ fontSize: 12, marginLeft: 4, color: '#8c8c8c' }}>{item.suffix}</span> : null}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Tabs defaultActiveKey="policy">
                    {/* Tab 1: AI 开放策略 */}
                    <TabPane tab={<span><LockOutlined /> AI 开放策略</span>} key="policy">
                        <Card title="全局 AI 访问策略" style={{ marginBottom: 20 }}>
                            <Alert
                                message="该策略决定哪些用户可以使用系统提供的公共 AI 服务，不影响使用个人 API Key 的用户"
                                type="info" showIcon style={{ marginBottom: 20 }}
                            />
                            <Radio.Group
                                value={aiPolicy}
                                onChange={e => handleSavePolicy(e.target.value)}
                                disabled={policyLoading}
                            >
                                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                    {policyOptions.map(opt => (
                                        <Card
                                            key={opt.value}
                                            size="small"
                                            style={{
                                                border: aiPolicy === opt.value ? `2px solid ${opt.color === 'green' ? '#52c41a' : opt.color === 'blue' ? '#1677ff' : opt.color === 'cyan' ? '#13c2c2' : '#fa8c16'}` : '1px solid #f0f0f0',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                background: aiPolicy === opt.value ? '#fafeff' : '#fff'
                                            }}
                                            onClick={() => !policyLoading && handleSavePolicy(opt.value)}
                                            bodyStyle={{ padding: '12px 16px' }}
                                        >
                                            <Space>
                                                <Radio value={opt.value} />
                                                <Tag color={opt.color} icon={opt.icon}>{opt.label}</Tag>
                                                <Text type="secondary" style={{ fontSize: 12 }}>{opt.desc}</Text>
                                            </Space>
                                        </Card>
                                    ))}
                                </Space>
                            </Radio.Group>
                            {aiPolicy === 'points' && (
                                <Card
                                    size="small"
                                    style={{ marginTop: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}
                                >
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        <Text strong>系统 AI 积分门槛</Text>
                                        <InputNumber
                                            min={0}
                                            step={25}
                                            value={pointsThreshold}
                                            onChange={(value) => setPointsThreshold(Math.max(Number(value) || 0, 0))}
                                            style={{ width: 220 }}
                                            addonAfter="积分"
                                        />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            只有积分大于等于该门槛的用户，才可使用系统平台统一配置的 AI 模型。
                                        </Text>
                                        <Button
                                            type="primary"
                                            onClick={() => handleSavePolicy('points', pointsThreshold)}
                                            loading={policyLoading}
                                        >
                                            保存积分门槛
                                        </Button>
                                    </Space>
                                </Card>
                            )}
                        </Card>

                        {/* 套餐 AI 权限配置 */}
                        <Card
                            title={<Space><CrownOutlined /> <span>各套餐 AI 权限配置</span></Space>}
                            extra={<Text type="secondary" style={{ fontSize: 12 }}>当策略为“仅会员可用”时，下方配置立即生效</Text>}
                        >
                            <Alert
                                message="可为每个会员套餐单独设定 AI 可用权限、月 Token 上限和积分消耗价格"
                                type="success" showIcon style={{ marginBottom: 16 }}
                            />
                            <Table
                                dataSource={membershipPlans}
                                columns={planColumns}
                                rowKey="id"
                                pagination={false}
                                size="middle"
                            />
                        </Card>
                    </TabPane>

                    {/* Tab 2: 全局 API Key 配置 */}
                    <TabPane tab={<span><KeyOutlined /> 全局模型 API Key</span>} key="providers">
                        <Alert
                            message="这里配置的 API Key 将作为系统统一密钥。未配置个人 Key 的用户，在调用系统 AI 时会共用这里的设置"
                            type="warning" showIcon icon={<WarningOutlined />} style={{ marginBottom: 16 }}
                        />
                        <Layout style={{ background: 'transparent', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                            {/* 左侧供应商列表 */}
                            <Sider width={210} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
                                <div style={{ padding: '10px 10px 6px', borderBottom: '1px solid #f0f0f0' }}>
                                    <Input
                                        prefix={<SearchOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />}
                                        placeholder="搜索供应商..."
                                        size="small"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        allowClear
                                    />
                                </div>
                                <div style={{ overflowY: 'auto', maxHeight: 480, padding: '6px 0' }}>
                                    {groups.map(group => {
                                        const items = filteredProviders.filter(p => p.group === group);
                                        if (!items.length) return null;
                                        return (
                                            <div key={group}>
                                                <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                                    {group}
                                                </div>
                                                {items.map(p => {
                                                    const hasGlobal = globalConfigs.some(c => c.provider_name === p.id && c.is_active);
                                                    const isSelected = selectedId === p.id;
                                                    return (
                                                        <div
                                                            key={p.id}
                                                            onClick={() => setSelectedId(p.id)}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 7,
                                                                padding: '6px 10px', cursor: 'pointer', borderRadius: 6,
                                                                margin: '1px 5px',
                                                                background: isSelected ? '#e6f4ff' : 'transparent',
                                                                color: isSelected ? '#1677ff' : '#333',
                                                            }}
                                                        >
                                                            <span style={{ fontSize: 15 }}>{p.icon}</span>
                                                            <span style={{ flex: 1, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {p.name}
                                                            </span>
                                                            {hasGlobal && (
                                                                <Tooltip title="全局已启用">
                                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }} />
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', fontSize: 10, color: '#aaa' }}>
                                    <Space size={4}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#52c41a", display: "inline-block" }} />已启用全局配置</Space>
                                </div>
                            </Sider>

                            {/* 右侧配置表单 */}
                            <Content style={{ padding: '20px 24px', background: '#fafafa' }}>
                                {selectedPreset && (
                                    <div style={{ maxWidth: 600 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                            <span style={{ fontSize: 30 }}>{selectedPreset.icon}</span>
                                            <div style={{ flex: 1 }}>
                                                <Title level={5} style={{ margin: 0 }}>{globalRecord?.display_name || selectedPreset.name}</Title>
                                                <Text type="secondary">系统全局配置状态</Text>
                                            </div>
                                            {globalRecord && (
                                                <Popconfirm
                                                    title="确认删除该供应商的全局配置？"
                                                    onConfirm={handleDeleteGlobal}
                                                    okText="删除" okType="danger" cancelText="取消"
                                                >
                                                    <Button size="small" danger icon={<DeleteOutlined />} loading={deleting}>删除</Button>
                                                </Popconfirm>
                                            )}
                                        </div>

                                        <Form form={globalForm} layout="vertical" onFinish={handleSaveGlobal}>
                                            <Form.Item label="显示名称" name="display_name">
                                                <Input placeholder={selectedPreset.name} />
                                            </Form.Item>

                                            <Form.Item
                                                label={
                                                    <Space>
                                                        <span>系统全局 API Key（所有用户共用）</span>
                                                        {globalRecord?.api_key && <Tag color="green" icon={<CheckCircleOutlined />}>已配置</Tag>}
                                                    </Space>
                                                }
                                                name="api_key"
                                                rules={[{ required: !globalRecord, message: "请输入 API Key" }]}
                                            >
                                                <Input.Password
                                                    placeholder={globalRecord ? "已配置，输入新值可覆盖" : "sk-... 或其他格式"}
                                                    prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />}
                                                />
                                            </Form.Item>

                                            <Form.Item label="API Base URL" name="api_base">
                                                <Input prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />} />
                                            </Form.Item>

                                            {/* 模型列表 */}
                                            <Form.Item label={
                                                <span>
                                                    支持的模型 <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>（点击标签快速添加）</Text>
                                                </span>
                                            }>
                                                <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {selectedPreset.models.map(m => (
                                                        <Tag key={m} style={{ cursor: 'pointer', fontSize: 11 }}
                                                            onClick={() => {
                                                                try {
                                                                    const arr = JSON.parse(customModelsStr || '[]');
                                                                    if (!arr.includes(m)) setCustomModelsStr(JSON.stringify([...arr, m]));
                                                                } catch { setCustomModelsStr(JSON.stringify([m])); }
                                                            }}>+ {m}
                                                        </Tag>
                                                    ))}
                                                </div>
                                                {selectedPreset.isCustomEntry && (
                                                    <Alert
                                                        type="info"
                                                        showIcon
                                                        style={{ marginBottom: 8 }}
                                                        message="自定义供应商没有预设模型，请手动填写该接口真实支持的模型名。"
                                                        description='示例：["gpt-4o", "gpt-4o-mini"] 或 ["gpt-5.3-codex"]'
                                                    />
                                                )}
                                                <Input.TextArea
                                                    rows={2}
                                                    value={customModelsStr}
                                                    onChange={e => setCustomModelsStr(e.target.value)}
                                                    placeholder='["model-1", "model-2"]'
                                                    style={{ fontFamily: 'monospace', fontSize: 11 }}
                                                />
                                            </Form.Item>

                                            <Form.Item label="启用该供应商" name="is_active" valuePropName="checked">
                                                <Switch checkedChildren="启用" unCheckedChildren="停用" />
                                            </Form.Item>

                                            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                                <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />} size="large">
                                                    保存全局配置
                                                </Button>
                                            </Form.Item>
                                        </Form>
                                    </div>
                                )}
                            </Content>
                        </Layout>
                    </TabPane>
                </Tabs>
            </div>

            {/* 套餐编辑弹窗 */}
            <Modal
                title={<Space><CrownOutlined /> <span>配置套餐 AI 权限 - {editingPlan?.name}</span></Space>}
                open={planEditVisible}
                onCancel={() => setPlanEditVisible(false)}
                footer={null}
                width={520}
            >
                <Form form={planForm} layout="vertical" onFinish={handleSavePlan}>
                    <Form.Item label="是否允许该套餐调用系统 AI" name="ai_access" valuePropName="checked">
                        <Switch checkedChildren="允许" unCheckedChildren="禁用" />
                    </Form.Item>

                    <Form.Item
                        label="每月 Token 限额"
                        name="ai_token_monthly"
                        extra="-1 表示无限制；建议普通版 30000，Pro 100000，Plus 300000，Max -1"
                        rules={[{ required: true, message: '请输入 Token 限额' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={-1}
                            step={10000}
                            formatter={v => v === -1 ? '无限' : (Number(v) || 0).toLocaleString()}
                            parser={v => (v === '无限' ? -1 : Number(String(v).replace(/,/g, '')))}
                        />
                    </Form.Item>

                    <Form.Item
                        label="积分消耗价格（积分 / 1K Token）"
                        name="ai_points_per_1k"
                        extra="0 = 免费，不消耗积分"
                    >
                        <InputNumber style={{ width: '100%' }} min={0} step={1} />
                    </Form.Item>

                    <Alert
                        message="调整后立即生效，用户下次调用 AI 时将使用新配置"
                        type="warning" showIcon style={{ marginBottom: 16 }}
                    />

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setPlanEditVisible(false)}>取消</Button>
                            <Button type="primary" htmlType="submit" loading={planSaving} icon={<SaveOutlined />}>保存</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default SystemAIConfig;





